import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover } from '@vd/ui';
import styles from './DateRangePicker.module.css';

/**
 * Calendar-style two-month range picker, Airbnb-flavored.
 *
 * - Click first day → range start. Click second day → range end.
 * - Clicking BEFORE the current start resets it to the new earlier date.
 * - Hover over a day after start (no end yet) → preview range highlight.
 * - Two months visible side-by-side; arrow buttons step the visible month.
 * - "Clear" zeroes both dates.
 *
 * Values are persisted as `YYYY-MM-DDTHH:MM:SS` local-time strings
 * (compatible with the `datetime-local` shape the api consumes). The
 * "from" side gets 00:00:00; the "to" side gets 23:59:59 so the
 * inclusive day-boundary feels natural.
 */
export interface DateRangePickerProps {
  /** ISO datetime string or empty. */
  from: string;
  /** ISO datetime string or empty. */
  to: string;
  onChange: (next: { from: string; to: string }) => void;
  /** Visible label when no range is set. */
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
}

function startOfDay(d: Date): Date { const c = new Date(d); c.setHours(0, 0, 0, 0); return c; }
function endOfDay(d: Date): Date { const c = new Date(d); c.setHours(23, 59, 59, 999); return c; }

function fmtIso(d: Date): string {
  // Pad helpers — `datetime-local` wants YYYY-MM-DDTHH:MM:SS exactly.
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/**
 * The first and last second of a clicked calendar day, written from its date
 * alone: the filters read them in the person's zone (TOADO-904), and the
 * browser's own clock must not move them. Where the browser's zone skips
 * midnight (Havana in March), a Date set to 00:00 reads 01:00 (Codex, round 4).
 */
export function dayStartIso(d: Date): string { return `${fmtIso(d).slice(0, 10)}T00:00:00`; }
export function dayEndIso(d: Date): string { return `${fmtIso(d).slice(0, 10)}T23:59:59`; }

function parseIso(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isFinite(d.getTime()) ? d : null;
}

// Splits an ISO datetime string into the YYYY-MM-DD and HH:MM parts
// the native <input type="date" /> + <input type="time" /> elements
// expect. Returning empty strings when the source is empty keeps the
// inputs in their unfilled state.
function dateOnly(s: string): string { return s ? s.slice(0, 10) : ''; }
function timeOnly(s: string): string { return s ? s.slice(11, 16) : ''; }

// Reassembles a YYYY-MM-DD + HH:MM pair into the canonical
// YYYY-MM-DDTHH:MM:SS shape, defaulting the seconds to the side-
// appropriate boundary so a typed date with no time still spans the
// inclusive day.
function assemble(date: string, time: string, side: 'from' | 'to'): string {
  if (!date) return '';
  const safeTime = time || (side === 'from' ? '00:00' : '23:59');
  const seconds = side === 'from' ? '00' : '59';
  return `${date}T${safeTime}:${seconds}`;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function fmtShort(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); // a day, not a moment (TOADO-904)
}

function addMonths(d: Date, n: number): Date {
  const c = new Date(d);
  c.setDate(1);
  c.setMonth(c.getMonth() + n);
  return c;
}

function monthGrid(monthStart: Date): Array<Date | null> {
  // 6-week grid (42 cells) so layout never reflows. First-of-month aligns
  // to its weekday column; preceding cells are null. Trailing cells past
  // the last day are also null.
  const year = monthStart.getFullYear();
  const month = monthStart.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay(); // 0 = Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: Array<Date | null> = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length < 42) cells.push(null);
  return cells;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function DateRangePicker({ from, to, onChange, placeholder = 'Select dates', className, ariaLabel }: DateRangePickerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);

  const fromDate = useMemo(() => parseIso(from), [from]);
  const toDate = useMemo(() => parseIso(to), [to]);

  // Visible left-month. Starts at the from-month if set, else this month.
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => {
    const seed = fromDate ?? new Date();
    return new Date(seed.getFullYear(), seed.getMonth(), 1);
  });

  // Hover preview: when from is set and to is not, hovering a day shows
  // a faint range highlight. Cleared on popover close.
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  // Re-prime the visible month each time the popover opens, so a freshly
  // opened picker centers on the user's current range (if any).
  useEffect(() => {
    if (!anchor) return;
    const seed = fromDate ?? new Date();
    setVisibleMonth(new Date(seed.getFullYear(), seed.getMonth(), 1));
    setHoverDate(null);
  }, [anchor, fromDate]);

  const onDayClick = (day: Date) => {
    if (!fromDate || (fromDate && toDate)) {
      // No selection yet, OR we already have a complete range — start a new one.
      onChange({ from: dayStartIso(day), to: '' });
      return;
    }
    // We have `from` but not `to`.
    if (day < fromDate) {
      // Clicking before the current start: reset start to the earlier date.
      onChange({ from: dayStartIso(day), to: '' });
      return;
    }
    if (sameDay(day, fromDate)) {
      // Same day click — collapse to a single-day range (start + same end).
      onChange({ from: from, to: dayEndIso(day) });
      return;
    }
    onChange({ from: from, to: dayEndIso(day) });
  };

  const clear = () => onChange({ from: '', to: '' });

  // Day-cell state helpers
  const isFromDay = (d: Date) => !!fromDate && sameDay(d, fromDate);
  const isToDay = (d: Date) => !!toDate && sameDay(d, toDate);
  const isInRange = (d: Date) => {
    if (!fromDate) return false;
    const end = toDate ?? hoverDate;
    if (!end) return false;
    if (end < fromDate) return false;
    return d >= startOfDay(fromDate) && d <= endOfDay(end);
  };

  const display = !fromDate
    ? placeholder
    : !toDate
      ? `${fmtShort(fromDate)} →`
      : sameDay(fromDate, toDate)
        ? fmtShort(fromDate)
        : `${fmtShort(fromDate)} to ${fmtShort(toDate)}`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={[styles.trigger, className].filter(Boolean).join(' ')}
        aria-label={ariaLabel ?? 'Pick date range'}
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        onClick={() => triggerRef.current && setAnchor(triggerRef.current.getBoundingClientRect())}
      >
        <span className={!fromDate ? styles.placeholder : undefined}>{display}</span>
        {fromDate && (
          <span
            className={styles.clearInline}
            role="button"
            tabIndex={0}
            aria-label="Clear date range"
            onClick={(e) => { e.stopPropagation(); clear(); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); clear(); } }}
          >×</span>
        )}
      </button>

      <Popover
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchor={anchor}
        width={620}
        estimatedHeight={520}
        withBackdrop
      >
        <div className={styles.calendarWrap} role="dialog" aria-label="Date range picker">
          <div className={styles.manualInputs}>
            <div className={styles.manualRow}>
              <span className={styles.manualLabel}>From</span>
              <input
                type="date"
                className={styles.manualInput}
                value={dateOnly(from)}
                onChange={(e) => onChange({ from: assemble(e.target.value, timeOnly(from), 'from'), to })}
                aria-label="From date"
              />
              <input
                type="time"
                className={`${styles.manualInput} ${styles.manualTime}`}
                value={timeOnly(from)}
                onChange={(e) => onChange({ from: assemble(dateOnly(from), e.target.value, 'from'), to })}
                aria-label="From time"
                disabled={!from}
              />
            </div>
            <div className={styles.manualRow}>
              <span className={styles.manualLabel}>To</span>
              <input
                type="date"
                className={styles.manualInput}
                value={dateOnly(to)}
                onChange={(e) => onChange({ from, to: assemble(e.target.value, timeOnly(to), 'to') })}
                aria-label="To date"
              />
              <input
                type="time"
                className={`${styles.manualInput} ${styles.manualTime}`}
                value={timeOnly(to)}
                onChange={(e) => onChange({ from, to: assemble(dateOnly(to), e.target.value, 'to') })}
                aria-label="To time"
                disabled={!to}
              />
            </div>
          </div>
          <div className={styles.headerRow}>
            <button type="button" className={styles.navBtn} onClick={() => setVisibleMonth((d) => addMonths(d, -1))} aria-label="Previous month">
              <ChevronLeft size={16} />
            </button>
            <div className={styles.monthsRow}>
              <MonthBlock
                monthStart={visibleMonth}
                isFromDay={isFromDay}
                isToDay={isToDay}
                isInRange={isInRange}
                onDayClick={onDayClick}
                onDayHover={setHoverDate}
              />
              <MonthBlock
                monthStart={addMonths(visibleMonth, 1)}
                isFromDay={isFromDay}
                isToDay={isToDay}
                isInRange={isInRange}
                onDayClick={onDayClick}
                onDayHover={setHoverDate}
              />
            </div>
            <button type="button" className={styles.navBtn} onClick={() => setVisibleMonth((d) => addMonths(d, 1))} aria-label="Next month">
              <ChevronRight size={16} />
            </button>
          </div>

          <div className={styles.footerRow}>
            <span className={styles.footerHint}>
              {!fromDate
                ? 'Click a day to start the range.'
                : !toDate
                  ? 'Click another day to finish. Clicking before the start resets it.'
                  : 'Pick again to start a new range.'}
            </span>
            <div className={styles.footerActions}>
              {(fromDate || toDate) && (
                <button type="button" className={styles.clearBtn} onClick={clear}>Clear</button>
              )}
              <button type="button" className={styles.doneBtn} onClick={() => setAnchor(null)}>Done</button>
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
}

function MonthBlock({
  monthStart, isFromDay, isToDay, isInRange, onDayClick, onDayHover,
}: {
  monthStart: Date;
  isFromDay: (d: Date) => boolean;
  isToDay: (d: Date) => boolean;
  isInRange: (d: Date) => boolean;
  onDayClick: (d: Date) => void;
  onDayHover: (d: Date | null) => void;
}) {
  const cells = useMemo(() => monthGrid(monthStart), [monthStart]);
  const label = monthStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }); // a day, not a moment (TOADO-904)
  const today = useMemo(() => startOfDay(new Date()), []);

  return (
    <div className={styles.month}>
      <div className={styles.monthLabel}>{label}</div>
      <div className={styles.weekdayRow}>
        {WEEKDAYS.map((w) => <div key={w} className={styles.weekday}>{w}</div>)}
      </div>
      <div className={styles.daysGrid}>
        {cells.map((d, i) => {
          if (!d) return <div key={i} className={styles.dayEmpty} />;
          const cls = [styles.day];
          if (isFromDay(d)) cls.push(styles.dayFrom);
          if (isToDay(d)) cls.push(styles.dayTo);
          if (isInRange(d) && !isFromDay(d) && !isToDay(d)) cls.push(styles.dayInRange);
          if (sameDay(d, today)) cls.push(styles.dayToday);
          return (
            <button
              key={i}
              type="button"
              className={cls.join(' ')}
              onClick={() => onDayClick(d)}
              onMouseEnter={() => onDayHover(d)}
              onMouseLeave={() => onDayHover(null)}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
